import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from './supabase';

// ══════════════════════════════════════════════════════════════
//  CATEGORY DEFINITIONS
// ══════════════════════════════════════════════════════════════
const CATS = {
  "Tile":                 { color:"#c9a96e", bg:"rgba(201,169,110,.12)", icon:"🏛️" },
  "Adhesive & Grout":     { color:"#6b8cae", bg:"rgba(107,140,174,.12)", icon:"🪣" },
  "Waterproofing":        { color:"#7b6bc4", bg:"rgba(123,107,196,.12)", icon:"🛡️" },
  "Subfloor & Prep":      { color:"#8c7b6e", bg:"rgba(140,123,110,.12)", icon:"📐" },
  "Lumber & Framing":     { color:"#946c00", bg:"rgba(148,108,0,.12)",   icon:"🪵" },
  "Hardware & Fasteners": { color:"#6b5e52", bg:"rgba(107,94,82,.12)",   icon:"🔩" },
  "Fixtures & Plumbing":  { color:"#0b7285", bg:"rgba(11,114,133,.12)",  icon:"🚿" },
  "Cabinets & Counters":  { color:"#c4954a", bg:"rgba(196,149,74,.12)",  icon:"🗄️" },
  "Equipment Rental":     { color:"#c92a2a", bg:"rgba(201,42,42,.12)",   icon:"🏗️" },
  "Electrical & Lighting":{ color:"#e67700", bg:"rgba(230,119,0,.12)",   icon:"💡" },
  "Glass":                { color:"#1a6b2a", bg:"rgba(26,107,42,.12)",   icon:"🪟" },
  "Other":                { color:"#8c7b6e", bg:"rgba(140,123,110,.12)", icon:"📦" },
};

// ══════════════════════════════════════════════════════════════
//  OPERATING EXPENSE CATEGORIES
// ══════════════════════════════════════════════════════════════
const OPEX_CATS = [
  { id:"vehicle",    label:"Vehicle & Fuel",           icon:"🚐", color:"#6b8cae" },
  { id:"insurance",  label:"Insurance",                icon:"🛡️", color:"#7b6bc4" },
  { id:"tools",      label:"Tools & Equipment",        icon:"🔧", color:"#946c00" },
  { id:"software",   label:"Software & Subscriptions", icon:"💻", color:"#0b7285" },
  { id:"marketing",  label:"Marketing & Advertising",  icon:"📣", color:"#c4954a" },
  { id:"office",     label:"Office & Admin",           icon:"📋", color:"#8c7b6e" },
  { id:"licenses",   label:"Licensing & Permits",      icon:"📜", color:"#c92a2a" },
  { id:"depreciation",label:"Depreciation & Amort.",   icon:"📉", color:"#6b5e52" },
  { id:"other_opex", label:"Other Overhead",           icon:"📦", color:"#4a4238" },
];

// ══════════════════════════════════════════════════════════════
//  PERIOD HELPERS
// ══════════════════════════════════════════════════════════════
const PERIODS = [
  { id:"all",         label:"All Time" },
  { id:"thisMonth",   label:"This Month" },
  { id:"lastMonth",   label:"Last Month" },
  { id:"thisQuarter", label:"This Quarter" },
  { id:"lastQuarter", label:"Last Quarter" },
  { id:"thisYear",    label:"This Year" },
  { id:"lastYear",    label:"Last Year" },
];

function getPeriodDates(periodId) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  switch(periodId) {
    case "thisMonth":   return { start: new Date(y,m,1), end: new Date(y,m+1,0,23,59,59) };
    case "lastMonth":   return { start: new Date(y,m-1,1), end: new Date(y,m,0,23,59,59) };
    case "thisQuarter": { const q=Math.floor(m/3); return { start: new Date(y,q*3,1), end: new Date(y,q*3+3,0,23,59,59) }; }
    case "lastQuarter": { const q=Math.floor(m/3)-1; const qy=q<0?y-1:y; const qm=((q%4)+4)%4*3; return { start: new Date(qy,qm,1), end: new Date(qy,qm+3,0,23,59,59) }; }
    case "thisYear":    return { start: new Date(y,0,1), end: new Date(y,11,31,23,59,59) };
    case "lastYear":    return { start: new Date(y-1,0,1), end: new Date(y-1,11,31,23,59,59) };
    default: return null;
  }
}

function getComparisonPeriod(periodId) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  switch(periodId) {
    case "thisMonth":   return { start: new Date(y,m-1,1), end: new Date(y,m,0,23,59,59), label:"vs last month" };
    case "lastMonth":   return { start: new Date(y,m-2,1), end: new Date(y,m-1,0,23,59,59), label:"vs prior month" };
    case "thisQuarter": { const q=Math.floor(m/3)-1; const qy=q<0?y-1:y; const qm=((q%4)+4)%4*3; return { start: new Date(qy,qm,1), end: new Date(qy,qm+3,0,23,59,59), label:"vs last quarter" }; }
    case "lastQuarter": { const q=Math.floor(m/3)-2; const qy=q<0?y-1:y; const qm=((q%4)+4)%4*3; return { start: new Date(qy,qm,1), end: new Date(qy,qm+3,0,23,59,59), label:"vs prior quarter" }; }
    case "thisYear":    return { start: new Date(y-1,0,1), end: new Date(y-1,11,31,23,59,59), label:"vs last year" };
    case "lastYear":    return { start: new Date(y-2,0,1), end: new Date(y-2,11,31,23,59,59), label:"vs prior year" };
    default: return null;
  }
}

function getPeriodLabel(periodId) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  const mName = n => new Date(2000,n).toLocaleString('en-US',{month:'short'});
  const qName = n => `Q${n+1}`;
  switch(periodId) {
    case "thisMonth":   return mName(m) + " " + y;
    case "lastMonth":   return mName(m-1<0?11:m-1) + " " + (m-1<0?y-1:y);
    case "thisQuarter": return qName(Math.floor(m/3)) + " " + y;
    case "lastQuarter": { const q=Math.floor(m/3)-1; return qName(q<0?3:q) + " " + (q<0?y-1:y); }
    case "thisYear":    return String(y);
    case "lastYear":    return String(y-1);
    default: return "All Time";
  }
}

const getMonthKey = d => { const dt=new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}`; };
const formatMonth = k => { const [y,m]=k.split('-'); return new Date(y,m-1).toLocaleString('en-US',{month:'short',year:'numeric'}); };

// ══════════════════════════════════════════════════════════════
//  FORMAT HELPERS
// ══════════════════════════════════════════════════════════════
const fmt = n=>"$"+Number(n||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtK = n=>Math.abs(n)>=1000?"$"+(n/1000).toFixed(1)+"k":fmt(n);
const fmtWhole = n=>"$"+Math.round(Number(n||0)).toLocaleString("en-US");

// ══════════════════════════════════════════════════════════════
//  PROJECT COMPUTATION — from budget data
// ══════════════════════════════════════════════════════════════
function computeFromBudget(row, job) {
  const phases = row.phases || [];
  let matBudget=0, matActual=0;
  const byCat={}, byPhase=[];

  phases.forEach(ph => {
    let pB=0, pA=0;
    (ph.items||[]).forEach(it => {
      const b = (Number(it.budgetQty)||0)*(Number(it.budgetPrice)||0);
      const a = (Number(it.actualQty)||0)*(Number(it.actualPrice)||0);
      matBudget+=b; matActual+=a; pB+=b; pA+=a;
      const cat = it.cat||"Other";
      if(!byCat[cat]) byCat[cat]={budget:0,actual:0};
      byCat[cat].budget+=b; byCat[cat].actual+=a;
    });
    byPhase.push({ name:ph.name||"Phase", budget:pB, actual:pA });
  });

  // Labor — phase-level if present, else project-level
  let labor=0, laborBudgetTotal=0;
  let hasPhaseLabor = phases.some(ph=>(Number(ph.laborBudget)||0)>0||(Number(ph.laborActual)||0)>0);
  if(hasPhaseLabor) {
    phases.forEach(ph=>{
      const actual = Number(ph.laborActual)||0;
      const budget = Number(ph.laborBudget)||0;
      labor += actual > 0 ? actual : budget; // Use actual if entered, otherwise use budget
      laborBudgetTotal += budget;
    });
  } else {
    labor = (row.labor_type||row.laborType)==="flat"
      ? (Number(row.labor_flat||row.laborFlat)||0)
      : (Number(row.labor_rate||row.laborRate)||0)*(Number(row.labor_hours||row.laborHours)||0);
    laborBudgetTotal = labor;
  }

  const quote = (Number(row.original_quote||row.originalQuote)||0)+(Number(row.change_order||row.changeOrder)||0) || (Number(job?.value)||0);
  const totalCost = matActual + labor;
  const grossProfit = quote - totalCost;
  const margin = quote>0 ? Math.round(grossProfit/quote*100) : 0;

  return {
    id: row.id,
    name: row.name || job?.project || "Unknown",
    customer: row.customer || job?.customer || "",
    location: row.location || job?.location || "",
    status: job?.status || "Unknown",
    budgetStatus: row.budget_status || row.status || (job ? "active" : "planning"),
    jobId: row.job_id,
    laborType: hasPhaseLabor ? "phase" : (row.labor_type||row.laborType||"flat"),
    laborRate: Number(row.labor_rate||row.laborRate)||0,
    laborHours: Number(row.labor_hours||row.laborHours)||0,
    laborFlat: Number(row.labor_flat||row.laborFlat)||0,
    originalQuote: Number(row.original_quote||row.originalQuote)||0,
    changeOrder: Number(row.change_order||row.changeOrder)||0,
    date: job?.start_date || job?.end_date || job?.created_at || row.created_at,
    matBudget, matActual, labor, laborBudgetTotal, quote, totalCost, grossProfit, margin,
    matVariance: matBudget - matActual,
    byCat, byPhase,
    hasBudget: true,
    crew: job?.crewName || job?.crew || "",
  };
}

function computeFromJob(job) {
  const revenue = Number(job.value)||0;
  const cost = Number(job.cost)||0;
  const grossProfit = revenue - cost;
  const margin = revenue>0 ? Math.round(grossProfit/revenue*100) : 0;
  return {
    id: job.id,
    name: job.project||"Unknown",
    customer: job.customer||"",
    location: job.location||"",
    status: job.status||"Unknown",
    budgetStatus: "active",
    jobId: job.id,
    laborType: "unknown", changeOrder: 0,
    date: job.start_date || job.end_date || job.created_at,
    matBudget:0, matActual:0, labor: cost>0?cost:0, laborBudgetTotal:0,
    quote: revenue, totalCost: cost, grossProfit, margin,
    matVariance: 0,
    byCat: {}, byPhase: [],
    hasBudget: false,
    crew: job.crewName || job.crew || "",
  };
}

// ══════════════════════════════════════════════════════════════
//  AGGREGATE HELPERS
// ══════════════════════════════════════════════════════════════
function computeTotals(projects) {
  const totRevenue   = projects.reduce((s,p)=>s+p.quote,0);
  const totMatBudget = projects.reduce((s,p)=>s+p.matBudget,0);
  const totMatActual = projects.reduce((s,p)=>s+p.matActual,0);
  const totLabor     = projects.reduce((s,p)=>s+p.labor,0);
  const totCost      = totMatActual+totLabor;
  const totGP        = totRevenue-totCost;
  const avgMargin    = totRevenue>0?Math.round(totGP/totRevenue*100):0;
  const totVariance  = totMatBudget-totMatActual;

  const projsWithCost = projects.filter(p=>p.totalCost>0);
  const overBudget  = projsWithCost.filter(p=>p.matVariance<0).length;
  const underBudget = projsWithCost.filter(p=>p.matVariance>=0).length;
  const completed   = projects.filter(p=>p.status==="Completed");
  const completedGP = completed.reduce((s,p)=>s+p.grossProfit,0);
  const completedRev= completed.reduce((s,p)=>s+p.quote,0);
  const completedMargin = completedRev>0?Math.round(completedGP/completedRev*100):0;

  const ranked = [...projects].filter(p=>p.quote>0).sort((a,b)=>b.margin-a.margin);
  const bestProj = ranked[0]||null;
  const worstProj = ranked.length>1?ranked[ranked.length-1]:null;

  const catRollup = {};
  projects.forEach(p=>{
    Object.entries(p.byCat).forEach(([cat,v])=>{
      if(!catRollup[cat]) catRollup[cat]={budget:0,actual:0,projects:new Set()};
      catRollup[cat].budget+=v.budget; catRollup[cat].actual+=v.actual;
      if(v.actual>0||v.budget>0) catRollup[cat].projects.add(p.name);
    });
  });
  const topCat = Object.entries(catRollup).sort((a,b)=>b[1].actual-a[1].actual)[0]||null;

  return { totRevenue, totMatBudget, totMatActual, totLabor, totCost, totGP, avgMargin, totVariance, overBudget, underBudget, completedMargin, bestProj, worstProj, catRollup, topCat, count:projects.length };
}

// ══════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════
export default function EnhancedPLDashboard() {
  const [view, setView]           = useState("overview");
  const [period, setPeriod]       = useState("all");
  const [expandedProject, setExpandedProject] = useState(null);
  const [sortBy, setSortBy]       = useState("margin");
  const [sortDir, setSortDir]     = useState("desc");
  const [plFilter, setPlFilter]   = useState("active"); // active | planning | archived | all
  const [revenueMode, setRevenueMode] = useState("contract"); // contract | collected

  // Data
  const [jobs, setJobs]           = useState([]);
  const [budgets, setBudgets]     = useState([]);
  const [invoices, setInvoices]   = useState([]);
  const [opex, setOpex]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [opexReady, setOpexReady] = useState(true); // false if table doesn't exist

  // OPEX modal
  const [opexModal, setOpexModal] = useState(null); // month string or null
  const [opexForm, setOpexForm]   = useState({});
  const [opexSaving, setOpexSaving] = useState(false);

  // ── Data Fetch ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, budgetsRes, invoicesRes] = await Promise.all([
        // NOTE: there is no `deleted_at` column on jobs — soft deletes live in
        // React state / localStorage trash, not the DB. Filtering on it here
        // returned 400 on every load and left this dashboard showing zero jobs.
        supabase.from('jobs').select('*').order('id',{ascending:false}),
        supabase.from('project_budgets').select('*').order('created_at'),
        supabase.from('invoices').select('*').order('id'),
      ]);
      if (jobsRes.error)    console.warn('Jobs fetch error:', jobsRes.error.message);
      if (budgetsRes.error) console.warn('Budget fetch error:', budgetsRes.error.message);
      setJobs(jobsRes.data||[]);
      setBudgets(budgetsRes.data||[]);
      if (invoicesRes.error) console.warn('Invoice fetch error:', invoicesRes.error.message);
      setInvoices(invoicesRes.data||[]);

      // Operating expenses — table may not exist yet
      const opexRes = await supabase.from('operating_expenses').select('*');
      if(opexRes.error) {
        console.log('operating_expenses table not found — EBITDA will use estimates');
        setOpexReady(false);
        setOpex([]);
      } else {
        setOpex(opexRes.data||[]);
        setOpexReady(true);
      }
    } catch(err) {
      console.error('P&L data fetch error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Build project list from budgets + jobs ──
  const allProjects = useMemo(() => {
    const budgetJobIds = new Set(budgets.filter(b=>b.job_id).map(b=>String(b.job_id)));

    const budgetProjects = budgets.map(b => {
      const job = b.job_id ? jobs.find(j=>j.id===b.job_id) : null;
      return computeFromBudget(b, job);
    });

    // For jobs without budgets: use job.value if set, otherwise pull contractTotal from linked invoice
    const jobOnly = jobs
      .filter(j => !budgetJobIds.has(String(j.id)))
      .map(j => {
        let revenue = Number(j.value) || 0;
        // If job has no value, try to get it from a linked invoice
        if (revenue <= 0) {
          const linkedInv = invoices.find(inv => {
            if (inv.job_id && String(inv.job_id) === String(j.id)) return true;
            if (inv.jobId && String(inv.jobId) === String(j.id)) return true;
            const estId = j.estimateId || j.estimate_id || j.estimateid;
            if (estId && String(inv.estimate_id || inv.estimateId) === String(estId)) return true;
            // Customer name fallback
            if (inv.customer && j.customer && inv.customer.toLowerCase().trim() === j.customer.toLowerCase().trim()) return true;
            return false;
          });
          if (linkedInv) revenue = Number(linkedInv.contractTotal || linkedInv.contract_total) || 0;
        }
        if (revenue <= 0) return null;
        return computeFromJob({ ...j, value: revenue });
      })
      .filter(Boolean);

    // Catch orphan invoices — invoices with contractTotal but no matching job or budget
    const allProjectJobIds = new Set([
      ...budgetProjects.map(p => String(p.jobId)),
      ...jobOnly.map(p => String(p.jobId)),
    ]);
    const allProjectCustomers = new Set([
      ...budgetProjects.map(p => (p.customer||'').toLowerCase().trim()),
      ...jobOnly.map(p => (p.customer||'').toLowerCase().trim()),
    ]);

    const orphanInvoices = [];
    const seenOrphanCustomers = new Set();
    invoices.forEach(inv => {
      const contractTotal = Number(inv.contractTotal || inv.contract_total) || 0;
      if (contractTotal <= 0) return;

      // Check if this invoice is already covered by an existing project
      const jid = String(inv.job_id || inv.jobId || '');
      if (jid && allProjectJobIds.has(jid)) return;
      const custKey = (inv.customer||'').toLowerCase().trim();
      if (custKey && allProjectCustomers.has(custKey)) return;
      if (seenOrphanCustomers.has(custKey)) return;
      seenOrphanCustomers.add(custKey);

      orphanInvoices.push({
        id: `orphan-${inv.id}`,
        name: inv.project || "Unknown",
        customer: inv.customer || "",
        location: "",
        status: "Completed",
        budgetStatus: "active",
        jobId: `orphan-${inv.id}`,
        laborType: "unknown", changeOrder: 0, originalQuote: contractTotal,
        date: inv.issued || inv.created_at,
        matBudget:0, matActual:0, labor:0, laborBudgetTotal:0,
        quote: contractTotal, totalCost:0,
        grossProfit: contractTotal, margin: 100,
        matVariance:0, byCat:{}, byPhase:[],
        hasBudget: false,
        crew: "",
      });
    });

    return [...budgetProjects, ...jobOnly, ...orphanInvoices];
  }, [jobs, budgets, invoices]);

  // ── Period filtering ──
  const filterByPeriod = useCallback((projects, dates) => {
    if(!dates) return projects;
    return projects.filter(p => {
      const d = p.date ? new Date(p.date) : null;
      if(!d || isNaN(d)) return false;
      return d >= dates.start && d <= dates.end;
    });
  }, []);

  const statusFilteredProjects = useMemo(() => {
    if (plFilter === "all") return allProjects;
    return allProjects.filter(p => p.budgetStatus === plFilter);
  }, [allProjects, plFilter]);

  const filteredProjects = useMemo(() =>
    filterByPeriod(statusFilteredProjects, getPeriodDates(period)),
  [statusFilteredProjects, period, filterByPeriod]);

  // ── Collected revenue by job (from actual invoice payments) ──
  const collectedByJob = useMemo(() => {
    const map = {};

    // Build estimate_id → job.id lookup so we can match invoices linked via estimates
    const estimateToJob = {};
    jobs.forEach(j => {
      const estId = j.estimateId || j.estimate_id || j.estimateid;
      if (estId) estimateToJob[String(estId)] = String(j.id);
    });

    // Also build customer → job.id lookup as a last-resort fallback
    const customerToJob = {};
    jobs.forEach(j => {
      const name = (j.customer || '').toLowerCase().trim();
      if (name) customerToJob[name] = String(j.id);
    });

    invoices.forEach(inv => {
      // Direct job link (snake_case from Supabase, camelCase from CRM state)
      let jid = inv.job_id || inv.jobId;

      // Fallback: match via estimate_id → job
      if (!jid && (inv.estimate_id || inv.estimateId)) {
        jid = estimateToJob[String(inv.estimate_id || inv.estimateId)];
      }

      // Last resort: match via customer name
      if (!jid && inv.customer) {
        jid = customerToJob[(inv.customer || '').toLowerCase().trim()];
      }

      if (!jid) return;
      const key = String(jid);

      const paid = (inv.payments || [])
        .filter(p => p.status === "Paid")
        .reduce((s, p) => s + (Number(p.amount) || 0), 0);
      if (paid > 0) {
        map[key] = (map[key] || 0) + paid;
      }
    });
    return map;
  }, [invoices, jobs]);

  // ── Apply revenue mode to a list of projects ──
  const applyRevenueMode = useCallback((projects) => {
    if (revenueMode === "contract") return projects;
    return projects.map(p => {
      const collected = collectedByJob[String(p.jobId)] || 0;
      const totalCost = p.matActual + p.labor;
      const grossProfit = collected - totalCost;
      const margin = collected > 0 ? Math.round(grossProfit / collected * 100) : 0;
      return { ...p, quote: collected, totalCost, grossProfit, margin };
    });
  }, [revenueMode, collectedByJob]);

  // ── Display-ready projects (applies revenue mode) ──
  const displayProjects = useMemo(() => applyRevenueMode(filteredProjects), [filteredProjects, applyRevenueMode]);

  // ── Current period totals ──
  const current = useMemo(() => computeTotals(displayProjects), [displayProjects]);

  // ── Comparison period totals ──
  const comparison = useMemo(() => {
    const comp = getComparisonPeriod(period);
    if(!comp) return null;
    const compProjects = applyRevenueMode(filterByPeriod(allProjects, comp));
    return { ...computeTotals(compProjects), label: comp.label };
  }, [allProjects, period, filterByPeriod, applyRevenueMode]);

  // ── Filtered + sorted projects ──
  const sortedProjects = useMemo(() => {
    const sorted = [...displayProjects];
    const dir = sortDir==="asc"?1:-1;
    sorted.sort((a,b) => {
      switch(sortBy) {
        case "revenue": return dir*(a.quote-b.quote);
        case "profit":  return dir*(a.grossProfit-b.grossProfit);
        case "name":    return dir*a.name.localeCompare(b.name);
        case "status":  return dir*a.status.localeCompare(b.status);
        default:        return dir*(a.margin-b.margin);
      }
    });
    return sorted;
  }, [displayProjects, sortBy, sortDir]);

  // ── Monthly EBITDA data ──
  const monthlyData = useMemo(() => {
    const byMonth = {};
    displayProjects.forEach(p => {
      if(!p.date) return;
      const key = getMonthKey(p.date);
      if(!byMonth[key]) byMonth[key]={revenue:0,materials:0,labor:0,projects:0};
      byMonth[key].revenue   += p.quote;
      byMonth[key].materials += p.matActual;
      byMonth[key].labor     += p.labor;
      byMonth[key].projects  += 1;
    });

    // Overlay operating expenses
    const periodDates = getPeriodDates(period);
    const filteredOpex = periodDates
      ? opex.filter(e => { const [y,m]=e.month.split('-'); const d=new Date(y,m-1,15); return d>=periodDates.start && d<=periodDates.end; })
      : opex;

    filteredOpex.forEach(e => {
      if(!byMonth[e.month]) byMonth[e.month]={revenue:0,materials:0,labor:0,projects:0};
      if(!byMonth[e.month].opexItems) byMonth[e.month].opexItems=[];
      byMonth[e.month].opexItems.push(e);
    });

    return Object.entries(byMonth)
      .sort((a,b) => a[0].localeCompare(b[0]))
      .map(([month, d]) => {
        const totalOpex = (d.opexItems||[]).reduce((s,e)=>s+Number(e.amount||0),0);
        const grossProfit = d.revenue - d.materials - d.labor;
        return {
          month, label: formatMonth(month),
          ...d,
          grossProfit,
          totalOpex,
          ebitda: grossProfit - totalOpex,
          opexItems: d.opexItems||[],
        };
      });
  }, [displayProjects, opex, period]);

  // ── EBITDA totals for period ──
  const ebitdaTotals = useMemo(() => {
    const totalOpex = monthlyData.reduce((s,m)=>s+m.totalOpex,0);
    return {
      grossProfit: current.totGP,
      totalOpex,
      ebitda: current.totGP - totalOpex,
      ebitdaMargin: current.totRevenue>0 ? Math.round((current.totGP-totalOpex)/current.totRevenue*100) : 0,
    };
  }, [monthlyData, current]);

  // ── Labor breakdown aggregation (from phase-level laborBreakdown) ──
  const laborData = useMemo(() => {
    const byInstaller = {};
    const byProject = [];

    // Get the set of displayed project IDs for filtering
    const displayedIds = new Set(displayProjects.map(p => String(p.id)));

    budgets.forEach(b => {
      // Only include budgets that correspond to displayed projects
      if (!displayedIds.has(String(b.id))) return;

      const proj = displayProjects.find(p => String(p.id) === String(b.id));
      const projName = proj?.name || b.name || "Unknown";
      const projCustomer = proj?.customer || b.customer || "";
      const projDate = proj?.date || b.created_at;
      const projInstallers = {};
      let projTotalLabor = 0;

      (b.phases || []).forEach(ph => {
        (ph.laborBreakdown || []).forEach(bd => {
          const name = bd.installer || "Unassigned";
          const amt = Number(bd.amount) || 0;
          if (amt <= 0) return;

          // Global installer totals
          if (!byInstaller[name]) byInstaller[name] = { total: 0, projects: 0, projectSet: new Set() };
          byInstaller[name].total += amt;
          byInstaller[name].projectSet.add(String(b.id));

          // Per-project installer totals
          if (!projInstallers[name]) projInstallers[name] = 0;
          projInstallers[name] += amt;
          projTotalLabor += amt;
        });

        // If no breakdown, attribute phase labor to "Unassigned"
        if (!(ph.laborBreakdown || []).length) {
          const actual = Number(ph.laborActual) || 0;
          const budget = Number(ph.laborBudget) || 0;
          const amt = actual > 0 ? actual : budget;
          if (amt > 0) {
            if (!projInstallers["Unassigned"]) projInstallers["Unassigned"] = 0;
            projInstallers["Unassigned"] += amt;
            projTotalLabor += amt;
            if (!byInstaller["Unassigned"]) byInstaller["Unassigned"] = { total: 0, projects: 0, projectSet: new Set() };
            byInstaller["Unassigned"].total += amt;
            byInstaller["Unassigned"].projectSet.add(String(b.id));
          }
        }
      });

      if (projTotalLabor > 0) {
        byProject.push({
          id: b.id, name: projName, customer: projCustomer, date: projDate,
          total: projTotalLabor,
          installers: Object.entries(projInstallers).map(([name, amount]) => ({ name, amount })).sort((a, b) => b.amount - a.amount),
        });
      }
    });

    // Finalize installer counts
    Object.values(byInstaller).forEach(v => { v.projects = v.projectSet.size; delete v.projectSet; });

    const installerList = Object.entries(byInstaller)
      .map(([name, d]) => ({ name, ...d }))
      .sort((a, b) => b.total - a.total);

    const totalLabor = installerList.reduce((s, i) => s + i.total, 0);

    return { installerList, byProject: byProject.sort((a, b) => b.total - a.total), totalLabor };
  }, [budgets, displayProjects]);

  // ── OPEX handlers ──
  const openOpexModal = (month) => {
    const existing = opex.filter(e=>e.month===month);
    const form = {};
    OPEX_CATS.forEach(c => {
      const entry = existing.find(e=>e.category===c.id);
      form[c.id] = { amount: entry ? Number(entry.amount) : '', notes: entry?.notes||'', dbId: entry?.id||null };
    });
    setOpexForm(form);
    setOpexModal(month);
  };

  const saveOpex = async () => {
    if(!opexModal) return;
    setOpexSaving(true);
    try {
      for(const cat of OPEX_CATS) {
        const entry = opexForm[cat.id];
        const amount = Number(entry?.amount)||0;
        if(entry?.dbId) {
          if(amount > 0) {
            await supabase.from('operating_expenses').update({ amount, notes: entry.notes||'' }).eq('id', entry.dbId);
          } else {
            await supabase.from('operating_expenses').delete().eq('id', entry.dbId);
          }
        } else if(amount > 0) {
          await supabase.from('operating_expenses').insert({ month: opexModal, category: cat.id, amount, notes: entry.notes||'' });
        }
      }
      await fetchData();
      setOpexModal(null);
    } catch(err) {
      console.error('OPEX save error:', err);
    }
    setOpexSaving(false);
  };

  // ── CSV Export ──
  const downloadCSV = (filename, headers, rows) => {
    const escape = v => `"${String(v||'').replace(/"/g, '""')}"`;
    const csv = [headers.join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPLSummary = () => downloadCSV(
    `pl-summary-${getPeriodLabel(period).replace(/\s/g,'-')}-${new Date().toISOString().slice(0,10)}.csv`,
    ['Project','Customer','Location','Status','Budget Status','Crew','Revenue','Mat Budget','Mat Actual','Labor','Total Cost','Gross Profit','Margin %','Budget Variance','Has Budget'],
    sortedProjects.map(p => [p.name,p.customer,p.location,p.status,p.budgetStatus,p.crew,p.quote,p.matBudget,p.matActual,p.labor,p.totalCost,p.grossProfit,p.margin+'%',p.matVariance,p.hasBudget?'Yes':'No'])
  );

  const exportEBITDA = () => downloadCSV(
    `ebitda-${getPeriodLabel(period).replace(/\s/g,'-')}-${new Date().toISOString().slice(0,10)}.csv`,
    ['Month','Revenue','Materials','Labor','Gross Profit','Operating Expenses','EBITDA','# Projects'],
    monthlyData.map(m => [m.label,m.revenue,m.materials,m.labor,m.grossProfit,m.totalOpex,m.ebitda,m.projects])
  );

  const exportAll = () => { exportPLSummary(); setTimeout(exportEBITDA, 500); };

  // ── Helpers ──
  const marginColor = m=>m>=40?"var(--accent-green)":m>=25?"var(--accent-amber)":"var(--accent-red)";
  const marginLabel = m=>m>=40?"✓ Strong":m>=25?"⚠ Watch":"🚨 Low";
  const statusColor = s => ({Completed:"var(--accent-green)","In Progress":"var(--accent-blue)",Scheduled:"var(--warm-gold)","On Hold":"var(--stone-400)"})[s]||"var(--stone-400)";

  const delta = (curr, prev) => {
    if(!prev || prev===0) return null;
    const pct = Math.round(((curr-prev)/Math.abs(prev))*100);
    return { pct, up: pct>=0 };
  };

  // ═══════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════
  if(loading) return (
    <div style={{minHeight:"60vh",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12}}>
      <div style={{width:40,height:40,border:"3px solid var(--stone-200)",borderTopColor:"var(--warm-gold)",borderRadius:"50%",animation:"spin 1s linear infinite"}}/>
      <div style={{fontSize:14,color:"var(--stone-500)"}}>Loading financial data...</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const { totRevenue, totMatBudget, totMatActual, totLabor, totGP, avgMargin, totVariance, catRollup, topCat, bestProj, worstProj, overBudget, underBudget } = current;

  return (
    <div className="fade-in" style={{minHeight:"100vh"}}>
      {/* ════ HEADER ════ */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,flexWrap:"wrap",gap:12}}>
        <div>
          <h1 style={{fontFamily:"var(--font-display)",fontSize:32,fontWeight:600,color:"var(--stone-900)",margin:0}}>Profit & Loss Dashboard</h1>
          <p style={{fontSize:14,color:"var(--stone-500)",marginTop:4}}>Complete financial picture across all projects — revenue, costs, margins, and budget accuracy</p>
        </div>
        <button onClick={view==="ebitda"?exportEBITDA:view==="categories"?exportPLSummary:exportAll}
          style={{padding:"8px 18px",borderRadius:"var(--radius)",border:"1.5px solid var(--stone-300)",background:"white",color:"var(--stone-700)",fontSize:13,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:8,boxShadow:"var(--shadow-sm)"}}>
          📥 Export CSV
        </button>
      </div>

      {/* ════ PERIOD SELECTOR ════ */}
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16,flexWrap:"wrap"}}>
        <div style={{display:"flex",gap:0,background:"var(--stone-200)",borderRadius:"var(--radius)",padding:3}}>
          {PERIODS.map(p=>(
            <button key={p.id} onClick={()=>setPeriod(p.id)}
              style={{padding:"6px 14px",borderRadius:6,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",fontFamily:"var(--font-body)",
                background:period===p.id?"white":"transparent",color:period===p.id?"var(--stone-900)":"var(--stone-500)",
                boxShadow:period===p.id?"0 1px 3px rgba(0,0,0,.1)":"none",transition:"all .2s",whiteSpace:"nowrap"}}>
              {p.label}
            </button>
          ))}
        </div>
        {period!=="all" && (
          <div style={{fontSize:12,color:"var(--stone-500)",fontWeight:500}}>
            Showing: <strong style={{color:"var(--stone-700)"}}>{getPeriodLabel(period)}</strong> · {filteredProjects.length} project{filteredProjects.length!==1?"s":""}
          </div>
        )}
      </div>

      {/* ════ BUDGET STATUS FILTER ════ */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <span style={{fontSize:12,color:"var(--stone-500)",fontWeight:600}}>Show:</span>
        {[["active","Active",allProjects.filter(p=>p.budgetStatus==="active").length],
          ["committed","Committed",allProjects.filter(p=>p.budgetStatus==="committed").length],
          ["planning","Planning",allProjects.filter(p=>p.budgetStatus==="planning").length],
          ["archived","Archived",allProjects.filter(p=>p.budgetStatus==="archived").length],
          ["all","All",allProjects.length]].map(([k,l,ct])=>(
          <button key={k} onClick={()=>setPlFilter(k)}
            style={{padding:"4px 14px",borderRadius:99,fontSize:11,fontWeight:600,cursor:"pointer",
              border:`1.5px solid ${plFilter===k?"var(--warm-gold)":"var(--stone-200)"}`,
              background:plFilter===k?"rgba(201,169,110,.1)":"white",color:plFilter===k?"var(--warm-gold)":"var(--stone-500)"}}>
            {l} {ct>0?`(${ct})`:""}
          </button>
        ))}
      </div>

      {/* ════ REVENUE MODE TOGGLE ════ */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <span style={{fontSize:12,color:"var(--stone-500)",fontWeight:600}}>Revenue:</span>
        {[["contract","📋 Contract Value"],["collected","💵 Cash Collected"]].map(([k,l])=>(
          <button key={k} onClick={()=>setRevenueMode(k)}
            style={{padding:"4px 14px",borderRadius:99,fontSize:11,fontWeight:600,cursor:"pointer",
              border:`1.5px solid ${revenueMode===k?"var(--accent-blue)":"var(--stone-200)"}`,
              background:revenueMode===k?"rgba(74,123,196,.1)":"white",color:revenueMode===k?"var(--accent-blue)":"var(--stone-500)"}}>
            {l}
          </button>
        ))}
        {revenueMode==="collected" && (
          <span style={{fontSize:11,color:"var(--accent-blue)",fontWeight:500,fontStyle:"italic"}}>
            Showing actual payments received — matches Invoicing page
          </span>
        )}
      </div>

      {/* ════ VIEW TABS ════ */}
      <div style={{display:"flex",gap:0,marginBottom:24,background:"var(--stone-200)",borderRadius:"var(--radius-lg)",padding:4,width:"fit-content"}}>
        {[["overview","📊 Overview"],["projects","📋 By Project"],["categories","🏷️ By Category"],["labor","👷 Labor"],["ebitda","💰 EBITDA"]].map(([v,label])=>(
          <button key={v} onClick={()=>setView(v)}
            style={{padding:"8px 20px",borderRadius:"var(--radius)",fontSize:13,fontWeight:600,cursor:"pointer",border:"none",fontFamily:"var(--font-body)",
              background:view===v?"white":"transparent",color:view===v?"var(--stone-900)":"var(--stone-500)",
              boxShadow:view===v?"0 1px 4px rgba(0,0,0,.1)":"none",transition:"all .2s"}}>
            {label}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {displayProjects.length===0 && view!=="ebitda" && (
        <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",border:"1.5px solid var(--stone-200)",padding:40,textAlign:"center"}}>
          <div style={{fontSize:32,marginBottom:8}}>📊</div>
          <div style={{fontFamily:"var(--font-display)",fontSize:20,fontWeight:700,marginBottom:8}}>No projects found for {getPeriodLabel(period)}</div>
          <div style={{fontSize:13,color:"var(--stone-500)"}}>Try a different time period or check that your jobs have dates set.</div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════ */}
      {/*  OVERVIEW VIEW                                          */}
      {/* ════════════════════════════════════════════════════════ */}
      {view === "overview" && displayProjects.length>0 && (<>
        {/* ── Top-line KPIs ── */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(175px,1fr))",gap:14,marginBottom:24}}>
          {[
            {label:"Total Revenue",   value:fmtK(totRevenue),  color:"var(--accent-blue)",  sub:revenueMode==="collected"?`${current.count} projects · cash received`:`${current.count} projects`, comp:comparison?delta(totRevenue,comparison.totRevenue):null },
            {label:"Material Spend",  value:fmtK(totMatActual),color:"var(--accent-red)",   sub:`Budget: ${fmtK(totMatBudget)}`, comp:comparison?delta(totMatActual,comparison.totMatActual):null },
            {label:"Total Labor",     value:fmtK(totLabor),    color:"var(--warm-gold)",     sub:"Across all projects", comp:comparison?delta(totLabor,comparison.totLabor):null },
            {label:"Gross Profit",    value:fmtK(totGP),       color:totGP>0?"var(--accent-green)":"var(--accent-red)", sub:"After materials + labor", comp:comparison?delta(totGP,comparison.totGP):null },
            {label:"Avg Margin",      value:avgMargin+"%",     color:marginColor(avgMargin), sub:marginLabel(avgMargin), comp:comparison?{pct:avgMargin-comparison.avgMargin,up:avgMargin>=comparison.avgMargin}:null },
            {label:"Budget Variance", value:(totVariance>=0?"+":"")+fmtK(totVariance), color:totVariance>=0?"var(--accent-green)":"var(--accent-red)", sub:totVariance>=0?"Under budget":"Over budget" },
          ].map((s,i)=>(
            <div key={i} style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",padding:"20px 22px",border:"1px solid var(--stone-200)",boxShadow:"var(--shadow-sm)"}}>
              <div style={{fontFamily:"var(--font-display)",fontSize:32,fontWeight:700,color:s.color,lineHeight:1}}>{s.value}</div>
              <div style={{fontSize:12,color:"var(--stone-500)",fontWeight:500,letterSpacing:".04em",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:3}}>
                <span style={{fontSize:11,color:"var(--stone-400)"}}>{s.sub}</span>
                {s.comp && comparison && (
                  <span style={{fontSize:10,fontWeight:700,color:s.comp.up?"var(--accent-green)":"var(--accent-red)"}}>
                    {s.comp.up?"↑":"↓"} {Math.abs(s.comp.pct)}{s.label==="Avg Margin"?"pt":"%"} <span style={{fontWeight:400,color:"var(--stone-400)"}}>{comparison.label}</span>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* ── Revenue Waterfall ── */}
        <div style={{background:"var(--stone-800)",borderRadius:"var(--radius-lg)",padding:24,marginBottom:20,color:"white"}}>
          <div style={{fontFamily:"var(--font-display)",fontSize:18,fontWeight:700,marginBottom:16}}>💰 Revenue Waterfall</div>
          <div style={{display:"flex",alignItems:"flex-end",gap:2,height:140,marginBottom:12}}>
            {[
              {label:"Revenue",  value:totRevenue,    color:"var(--accent-blue)"},
              {label:"Materials",value:-totMatActual,  color:"var(--accent-red)"},
              {label:"Labor",    value:-totLabor,      color:"var(--warm-gold)"},
              {label:"Profit",   value:totGP,          color:totGP>0?"var(--accent-green)":"var(--accent-red)"},
            ].map((bar,i)=>{
              const maxVal = Math.max(totRevenue,Math.abs(totGP),1);
              const h = Math.max(Math.round(Math.abs(bar.value)/maxVal*120),8);
              return(
                <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                  <div style={{fontSize:13,fontFamily:"var(--font-display)",fontWeight:700,color:bar.color}}>{fmtK(Math.abs(bar.value))}</div>
                  <div style={{width:"70%",height:h,background:bar.color,borderRadius:"4px 4px 0 0",transition:"height .5s ease"}}/>
                  <div style={{fontSize:10,opacity:.6,textAlign:"center"}}>{bar.value<0?"−":""}{bar.label}</div>
                </div>
              );
            })}
          </div>
          <div style={{borderTop:"1px solid rgba(255,255,255,.1)",paddingTop:12,display:"flex",justifyContent:"space-between",fontSize:12,flexWrap:"wrap",gap:8}}>
            <span style={{opacity:.5}}>Material cost ratio: {totRevenue>0?Math.round(totMatActual/totRevenue*100):0}% of revenue</span>
            <span style={{opacity:.5}}>Labor ratio: {totRevenue>0?Math.round(totLabor/totRevenue*100):0}% of revenue</span>
            <span style={{color:marginColor(avgMargin),fontWeight:700}}>Net margin: {avgMargin}%</span>
          </div>
        </div>

        {/* ── Two-column: Budget Accuracy + Category Spend ── */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
          {/* Budget Accuracy */}
          <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",border:"1px solid var(--stone-200)",overflow:"hidden"}}>
            <div style={{padding:"14px 20px",borderBottom:"1px solid var(--stone-100)",fontFamily:"var(--font-display)",fontSize:16,fontWeight:700}}>🎯 Budget Accuracy</div>
            <div style={{padding:"18px 20px"}}>
              <div style={{display:"flex",gap:12,marginBottom:16}}>
                <div style={{flex:1,background:"rgba(122,158,126,.08)",borderRadius:"var(--radius)",padding:14,textAlign:"center"}}>
                  <div style={{fontFamily:"var(--font-display)",fontSize:28,fontWeight:700,color:"var(--accent-green)"}}>{underBudget}</div>
                  <div style={{fontSize:11,color:"var(--stone-500)",marginTop:2}}>Under Budget</div>
                </div>
                <div style={{flex:1,background:"rgba(192,97,74,.08)",borderRadius:"var(--radius)",padding:14,textAlign:"center"}}>
                  <div style={{fontFamily:"var(--font-display)",fontSize:28,fontWeight:700,color:"var(--accent-red)"}}>{overBudget}</div>
                  <div style={{fontSize:11,color:"var(--stone-500)",marginTop:2}}>Over Budget</div>
                </div>
              </div>
              {displayProjects.filter(p=>p.matActual>0&&p.hasBudget).map(p=>{
                const pct = p.matBudget>0?Math.round(p.matActual/p.matBudget*100):0;
                const over = p.matActual>p.matBudget;
                return(
                  <div key={p.id} style={{marginBottom:12}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}>
                      <span style={{fontWeight:600}}>{p.customer ? `${p.customer} — ` : ""}{p.name}</span>
                      <span style={{fontWeight:700,color:over?"var(--accent-red)":"var(--accent-green)"}}>{pct}% {over?"(over)":"(under)"}</span>
                    </div>
                    <div style={{height:8,background:"var(--stone-100)",borderRadius:99,overflow:"hidden",position:"relative"}}>
                      <div style={{height:"100%",width:`${Math.min(pct,100)}%`,background:over?"var(--accent-red)":pct>90?"var(--accent-amber)":"var(--accent-green)",borderRadius:99,transition:"width .5s"}}/>
                      <div style={{position:"absolute",left:"100%",top:0,bottom:0,width:2,background:"var(--stone-300)",transform:"translateX(-2px)"}}/>
                    </div>
                    <div style={{fontSize:10,color:"var(--stone-400)",marginTop:2}}>Budget: {fmt(p.matBudget)} · Actual: {fmt(p.matActual)}</div>
                  </div>
                );
              })}
              {displayProjects.filter(p=>p.matActual>0&&p.hasBudget).length===0 && (
                <div style={{fontSize:12,color:"var(--stone-400)",fontStyle:"italic",textAlign:"center",padding:20}}>No projects with budget data in this period</div>
              )}
            </div>
          </div>

          {/* Category Spend */}
          <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",border:"1px solid var(--stone-200)",overflow:"hidden"}}>
            <div style={{padding:"14px 20px",borderBottom:"1px solid var(--stone-100)",fontFamily:"var(--font-display)",fontSize:16,fontWeight:700}}>🏷️ Spend by Category</div>
            <div style={{padding:"18px 20px"}}>
              {totMatActual>0 && (
                <div style={{display:"flex",height:16,borderRadius:99,overflow:"hidden",gap:1,marginBottom:14}}>
                  {Object.entries(catRollup).filter(([_,v])=>v.actual>0).sort((a,b)=>b[1].actual-a[1].actual).map(([cat,v])=>{
                    const ci=CATS[cat]||CATS.Other;
                    const pct=Math.max(Math.round(v.actual/totMatActual*100),1);
                    return <div key={cat} style={{width:`${pct}%`,background:ci.color,height:"100%"}} title={`${cat}: ${fmt(v.actual)}`}/>;
                  })}
                </div>
              )}
              {Object.entries(catRollup).filter(([_,v])=>v.actual>0).sort((a,b)=>b[1].actual-a[1].actual).map(([cat,v])=>{
                const ci=CATS[cat]||CATS.Other;
                const pct=totMatActual>0?Math.round(v.actual/totMatActual*100):0;
                return(
                  <div key={cat} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid var(--stone-100)"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{width:10,height:10,borderRadius:"50%",background:ci.color,flexShrink:0}}/>
                      <span style={{fontSize:12,fontWeight:600}}>{ci.icon} {cat}</span>
                    </div>
                    <div style={{display:"flex",gap:16,alignItems:"center"}}>
                      <span style={{fontSize:11,color:"var(--stone-400)"}}>{pct}%</span>
                      <span style={{fontSize:12,fontWeight:700,color:ci.color,minWidth:70,textAlign:"right"}}>{fmt(v.actual)}</span>
                    </div>
                  </div>
                );
              })}
              {totMatActual===0 && <div style={{fontSize:12,color:"var(--stone-400)",fontStyle:"italic",textAlign:"center",padding:20}}>No material spend recorded</div>}
            </div>
          </div>
        </div>

        {/* ── Insights Row ── */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,marginBottom:20}}>
          {bestProj && (
            <div style={{background:"rgba(122,158,126,.06)",borderRadius:"var(--radius-lg)",border:"1px solid rgba(122,158,126,.2)",padding:20}}>
              <div style={{fontSize:11,color:"var(--accent-green)",fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:6}}>🏆 Best Margin</div>
              <div style={{fontFamily:"var(--font-display)",fontSize:20,fontWeight:700}}>{bestProj.name}</div>
              <div style={{fontFamily:"var(--font-display)",fontSize:28,fontWeight:700,color:"var(--accent-green)",marginTop:4}}>{bestProj.margin}%</div>
              <div style={{fontSize:12,color:"var(--stone-500)",marginTop:4}}>Revenue: {fmt(bestProj.quote)} · Profit: {fmt(bestProj.grossProfit)}</div>
            </div>
          )}
          {worstProj && (
            <div style={{background:"rgba(192,97,74,.04)",borderRadius:"var(--radius-lg)",border:"1px solid rgba(192,97,74,.15)",padding:20}}>
              <div style={{fontSize:11,color:"var(--accent-red)",fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:6}}>⚠️ Lowest Margin</div>
              <div style={{fontFamily:"var(--font-display)",fontSize:20,fontWeight:700}}>{worstProj.name}</div>
              <div style={{fontFamily:"var(--font-display)",fontSize:28,fontWeight:700,color:marginColor(worstProj.margin),marginTop:4}}>{worstProj.margin}%</div>
              <div style={{fontSize:12,color:"var(--stone-500)",marginTop:4}}>Revenue: {fmt(worstProj.quote)} · Profit: {fmt(worstProj.grossProfit)}</div>
            </div>
          )}
          {topCat && (
            <div style={{background:"rgba(201,169,110,.06)",borderRadius:"var(--radius-lg)",border:"1px solid rgba(201,169,110,.2)",padding:20}}>
              <div style={{fontSize:11,color:"var(--warm-gold)",fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:6}}>💰 Biggest Cost Category</div>
              <div style={{fontFamily:"var(--font-display)",fontSize:20,fontWeight:700}}>{(CATS[topCat[0]]||CATS.Other).icon} {topCat[0]}</div>
              <div style={{fontFamily:"var(--font-display)",fontSize:28,fontWeight:700,color:"var(--warm-gold)",marginTop:4}}>{fmt(topCat[1].actual)}</div>
              <div style={{fontSize:12,color:"var(--stone-500)",marginTop:4}}>{totMatActual>0?Math.round(topCat[1].actual/totMatActual*100):0}% of material spend</div>
            </div>
          )}
        </div>

        {/* ── Enhanced KPIs: Performance Metrics ── */}
        {displayProjects.length >= 2 && (() => {
          const avgRevPerJob = current.count > 0 ? Math.round(totRevenue / current.count) : 0;
          const budgetedProjects = displayProjects.filter(p => p.hasBudget && p.matBudget > 0);
          const overrunProjects = budgetedProjects.filter(p => p.matActual > p.matBudget);
          const overrunRate = budgetedProjects.length > 0 ? Math.round((overrunProjects.length / budgetedProjects.length) * 100) : 0;

          const marginBuckets = [
            { label: "50%+", min: 50, max: 999, color: "var(--accent-green)", count: 0 },
            { label: "30–50%", min: 30, max: 50, color: "var(--accent-blue)", count: 0 },
            { label: "15–30%", min: 15, max: 30, color: "var(--accent-amber)", count: 0 },
            { label: "<15%", min: -999, max: 15, color: "var(--accent-red)", count: 0 },
          ];
          displayProjects.filter(p => p.quote > 0).forEach(p => {
            const bucket = marginBuckets.find(b => p.margin >= b.min && p.margin < b.max);
            if (bucket) bucket.count++;
          });
          const maxBucket = Math.max(...marginBuckets.map(b => b.count), 1);

          const atRiskJobs = displayProjects.filter(p =>
            (p.status === "In Progress" || p.status === "Scheduled") && p.quote > 0 && p.margin < 25 && p.totalCost > 0
          );

          const monthlyRevenue = {};
          allProjects.forEach(p => {
            if (!p.date) return;
            const key = getMonthKey(p.date);
            if (!monthlyRevenue[key]) monthlyRevenue[key] = { revenue: 0, count: 0 };
            monthlyRevenue[key].revenue += p.quote;
            monthlyRevenue[key].count++;
          });
          const recentMonths = Object.entries(monthlyRevenue)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .slice(-6)
            .map(([k, v]) => ({ month: k, label: formatMonth(k), ...v }));
          const maxMonthRev = Math.max(...recentMonths.map(m => m.revenue), 1);

          return (
            <>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(175px,1fr))",gap:14,marginBottom:20}}>
                <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",padding:"18px 20px",border:"1px solid var(--stone-200)",boxShadow:"var(--shadow-sm)"}}>
                  <div style={{fontFamily:"var(--font-display)",fontSize:28,fontWeight:700,color:"var(--accent-blue)",lineHeight:1}}>{fmtWhole(avgRevPerJob)}</div>
                  <div style={{fontSize:11,color:"var(--stone-500)",fontWeight:500,textTransform:"uppercase",letterSpacing:".04em",marginTop:5}}>Avg Revenue / Job</div>
                  <div style={{fontSize:10,color:"var(--stone-400)",marginTop:2}}>{current.count} project{current.count!==1?"s":""}</div>
                </div>
                <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",padding:"18px 20px",border:"1px solid var(--stone-200)",boxShadow:"var(--shadow-sm)"}}>
                  <div style={{fontFamily:"var(--font-display)",fontSize:28,fontWeight:700,color:overrunRate>40?"var(--accent-red)":overrunRate>20?"var(--accent-amber)":"var(--accent-green)",lineHeight:1}}>{overrunRate}%</div>
                  <div style={{fontSize:11,color:"var(--stone-500)",fontWeight:500,textTransform:"uppercase",letterSpacing:".04em",marginTop:5}}>Cost Overrun Rate</div>
                  <div style={{fontSize:10,color:"var(--stone-400)",marginTop:2}}>{overrunProjects.length} of {budgetedProjects.length} budgeted jobs</div>
                </div>
                <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",padding:"18px 20px",border:"1px solid var(--stone-200)",boxShadow:"var(--shadow-sm)"}}>
                  <div style={{fontFamily:"var(--font-display)",fontSize:28,fontWeight:700,color:current.completedMargin>0?"var(--accent-green)":"var(--stone-400)",lineHeight:1}}>{current.completedMargin}%</div>
                  <div style={{fontSize:11,color:"var(--stone-500)",fontWeight:500,textTransform:"uppercase",letterSpacing:".04em",marginTop:5}}>Completed Job Margin</div>
                  <div style={{fontSize:10,color:"var(--stone-400)",marginTop:2}}>Finished projects only</div>
                </div>
                {atRiskJobs.length > 0 && (
                  <div style={{background:"rgba(192,97,74,.04)",borderRadius:"var(--radius-lg)",padding:"18px 20px",border:"1px solid rgba(192,97,74,.2)",boxShadow:"var(--shadow-sm)"}}>
                    <div style={{fontFamily:"var(--font-display)",fontSize:28,fontWeight:700,color:"var(--accent-red)",lineHeight:1}}>{atRiskJobs.length}</div>
                    <div style={{fontSize:11,color:"var(--accent-red)",fontWeight:500,textTransform:"uppercase",letterSpacing:".04em",marginTop:5}}>Margin Alerts</div>
                    <div style={{fontSize:10,color:"var(--stone-400)",marginTop:2}}>Active jobs below 25%</div>
                  </div>
                )}
              </div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
                <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",border:"1px solid var(--stone-200)",overflow:"hidden"}}>
                  <div style={{padding:"14px 20px",borderBottom:"1px solid var(--stone-100)",fontFamily:"var(--font-display)",fontSize:16,fontWeight:700}}>📊 Margin Distribution</div>
                  <div style={{padding:"18px 20px"}}>
                    {marginBuckets.map(b => (
                      <div key={b.label} style={{marginBottom:12}}>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                          <span style={{fontWeight:600,color:b.color}}>{b.label} margin</span>
                          <span style={{fontWeight:700}}>{b.count} job{b.count!==1?"s":""}</span>
                        </div>
                        <div style={{height:10,background:"var(--stone-100)",borderRadius:99,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${(b.count/maxBucket)*100}%`,background:b.color,borderRadius:99,transition:"width .5s"}}/>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",border:"1px solid var(--stone-200)",overflow:"hidden"}}>
                  <div style={{padding:"14px 20px",borderBottom:"1px solid var(--stone-100)",fontFamily:"var(--font-display)",fontSize:16,fontWeight:700}}>📈 Revenue Trend</div>
                  <div style={{padding:"18px 20px"}}>
                    {recentMonths.length > 0 ? (
                      <div style={{display:"flex",alignItems:"flex-end",gap:6,height:120,marginBottom:8}}>
                        {recentMonths.map(m => {
                          const h = Math.max(Math.round((m.revenue / maxMonthRev) * 100), 6);
                          return (
                            <div key={m.month} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                              <div style={{fontSize:10,fontWeight:700,color:"var(--accent-blue)"}}>{fmtK(m.revenue)}</div>
                              <div style={{width:"80%",height:h,background:"var(--accent-blue)",borderRadius:"4px 4px 0 0",transition:"height .5s"}}/>
                              <div style={{fontSize:9,color:"var(--stone-400)",textAlign:"center",whiteSpace:"nowrap"}}>{m.label.split(' ')[0]}</div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{fontSize:12,color:"var(--stone-400)",textAlign:"center",padding:30}}>Not enough monthly data yet</div>
                    )}
                    {recentMonths.length >= 2 && (() => {
                      const last = recentMonths[recentMonths.length - 1]?.revenue || 0;
                      const prev = recentMonths[recentMonths.length - 2]?.revenue || 0;
                      const change = prev > 0 ? Math.round(((last - prev) / prev) * 100) : 0;
                      return change !== 0 ? (
                        <div style={{fontSize:11,color:"var(--stone-400)",textAlign:"center"}}>
                          Latest month: <span style={{fontWeight:700,color:change>0?"var(--accent-green)":"var(--accent-red)"}}>{change>0?"↑":"↓"} {Math.abs(change)}%</span> vs prior
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>

              {atRiskJobs.length > 0 && (
                <div style={{background:"rgba(192,97,74,.03)",borderRadius:"var(--radius-lg)",border:"1.5px solid rgba(192,97,74,.15)",padding:20,marginBottom:20}}>
                  <div style={{fontFamily:"var(--font-display)",fontSize:16,fontWeight:700,color:"var(--accent-red)",marginBottom:12}}>🚨 Margin Alerts — Active Jobs Below 25%</div>
                  <div style={{display:"grid",gap:8}}>
                    {atRiskJobs.map(p => (
                      <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:"white",borderRadius:"var(--radius)",border:"1px solid rgba(192,97,74,.15)"}}>
                        <div>
                          <div style={{fontWeight:600,fontSize:13}}>{p.name}</div>
                          <div style={{fontSize:11,color:"var(--stone-400)"}}>{p.customer} · {p.status}</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontFamily:"var(--font-display)",fontSize:18,fontWeight:700,color:marginColor(p.margin)}}>{p.margin}%</div>
                          <div style={{fontSize:10,color:"var(--stone-400)"}}>Revenue: {fmtK(p.quote)} · Profit: {fmtK(p.grossProfit)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        })()}

      </>)}

      {/* ════════════════════════════════════════════════════════ */}
      {/*  BY PROJECT VIEW                                        */}
      {/* ════════════════════════════════════════════════════════ */}
      {view === "projects" && displayProjects.length>0 && (<>
        {/* Sort controls */}
        <div style={{display:"flex",gap:8,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
          <span style={{fontSize:12,color:"var(--stone-500)",fontWeight:600}}>Sort by:</span>
          {[["margin","Margin"],["revenue","Revenue"],["profit","Profit"],["name","Name"],["status","Status"]].map(([k,l])=>(
            <button key={k} onClick={()=>{ if(sortBy===k) setSortDir(d=>d==="asc"?"desc":"asc"); else { setSortBy(k); setSortDir("desc"); }}}
              style={{padding:"4px 12px",borderRadius:99,fontSize:11,fontWeight:600,cursor:"pointer",border:`1.5px solid ${sortBy===k?"var(--warm-gold)":"var(--stone-200)"}`,
                background:sortBy===k?"rgba(201,169,110,.1)":"white",color:sortBy===k?"var(--warm-gold)":"var(--stone-500)"}}>
              {l} {sortBy===k?(sortDir==="desc"?"↓":"↑"):""}
            </button>
          ))}
        </div>

        {sortedProjects.map(p => {
          const expanded = expandedProject===p.id;
          const gpColor = marginColor(p.margin);
          const sc = statusColor(p.status);
          return (
            <div key={p.id} style={{borderRadius:"var(--radius-lg)",overflow:"hidden",marginBottom:16,border:"1px solid var(--stone-200)",boxShadow:"var(--shadow-sm)"}}>
              {/* Header */}
              <div onClick={()=>setExpandedProject(expanded?null:p.id)}
                style={{background:"var(--stone-800)",padding:"14px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",flexWrap:"wrap",gap:8}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:10,color:"var(--stone-400)",transition:"transform .2s",transform:expanded?"rotate(0)":"rotate(-90deg)"}}>▼</span>
                  <span style={{fontFamily:"var(--font-display)",fontSize:18,fontWeight:700,color:"white"}}>{p.name}</span>
                  <span style={{background:`${sc}33`,color:sc==="var(--accent-green)"?"#86efac":sc==="var(--accent-blue)"?"#93c5fd":"var(--warm-gold-light)",
                    padding:"2px 10px",borderRadius:99,fontSize:11,fontWeight:600}}>{p.status}</span>
                  {p.hasBudget && (
                    <select value={p.budgetStatus} onClick={e=>e.stopPropagation()} onChange={e=>{
                      const newStatus = e.target.value;
                      setBudgets(prev=>prev.map(b=>b.id===p.id?{...b,budget_status:newStatus,status:newStatus}:b));
                      supabase.from('project_budgets').update({budget_status:newStatus}).eq('id',p.id).then(({error})=>{
                        if(error) console.error('[P&L] Budget status update error:',error.message);
                      });
                    }}
                      style={{background:"rgba(255,255,255,.12)",color:"white",border:"1px solid rgba(255,255,255,.2)",borderRadius:99,
                        fontSize:10,fontWeight:600,padding:"2px 8px",cursor:"pointer",appearance:"auto"}}>
                      <option value="active">● Active</option>
                      <option value="committed">◉ Committed</option>
                      <option value="planning">◯ Planning</option>
                      <option value="archived">⊘ Archived</option>
                    </select>
                  )}
                  {!p.hasBudget && <span style={{background:"rgba(192,97,74,.2)",color:"#fca5a5",padding:"2px 8px",borderRadius:99,fontSize:10,fontWeight:600}}>No Budget</span>}
                </div>
                <div style={{display:"flex",gap:16,alignItems:"center"}}>
                  <span style={{fontSize:12,color:"var(--stone-400)"}}>{p.customer}</span>
                  <span style={{fontFamily:"var(--font-display)",fontSize:18,fontWeight:700,color:gpColor}}>{p.margin}%</span>
                </div>
              </div>

              {/* P&L cells */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",borderBottom:"1px solid var(--stone-100)"}}>
                {[
                  ["Revenue",fmt(p.quote),"var(--accent-blue)"],
                  ["Mat. Budget",p.hasBudget?fmt(p.matBudget):"—","var(--stone-600)"],
                  ["Mat. Actual",p.hasBudget?fmt(p.matActual):"—",p.matVariance<0?"var(--accent-red)":"var(--accent-green)"],
                  ["Labor",fmt(p.labor),"var(--warm-gold)",p.laborType==="phase"?"Per phase":p.laborType==="flat"?"Flat rate":p.laborType==="hourly"?`${p.laborHours||0}h × ${fmt(p.laborRate||0)}/hr`:""],
                  ["Gross Profit",fmt(p.grossProfit),gpColor],
                  ["Margin",p.margin+"%",gpColor,marginLabel(p.margin)],
                ].map(([label,val,color,sub],i)=>(
                  <div key={i} style={{padding:"14px 16px",borderRight:"1px solid var(--stone-100)"}}>
                    <div style={{fontSize:10,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",color:"var(--stone-400)",marginBottom:3}}>{label}</div>
                    <div style={{fontFamily:"var(--font-display)",fontSize:19,fontWeight:700,color}}>{val}</div>
                    {sub && <div style={{fontSize:10,color:"var(--stone-400)",marginTop:2}}>{sub}</div>}
                  </div>
                ))}
              </div>

              {/* Expanded detail */}
              {expanded && p.hasBudget && (
                <div style={{background:"var(--stone-50)",padding:20}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                    {/* Phase breakdown */}
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:"var(--stone-600)",textTransform:"uppercase",letterSpacing:".05em",marginBottom:10}}>By Phase</div>
                      {p.byPhase.filter(ph=>ph.budget>0||ph.actual>0).map((ph,i)=>{
                        const pct=p.matActual>0?Math.round(ph.actual/p.matActual*100):0;
                        return(
                          <div key={i} style={{marginBottom:10}}>
                            <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:2}}>
                              <span style={{fontWeight:600}}>{ph.name}</span>
                              <span style={{fontWeight:700}}>{fmt(ph.actual)}</span>
                            </div>
                            <div style={{height:6,background:"var(--stone-200)",borderRadius:99,overflow:"hidden"}}>
                              <div style={{height:"100%",width:`${pct}%`,background:"var(--warm-gold)",borderRadius:99}}/>
                            </div>
                            <div style={{fontSize:10,color:"var(--stone-400)",marginTop:1}}>Budget: {fmt(ph.budget)} · {pct}% of spend</div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Category breakdown */}
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:"var(--stone-600)",textTransform:"uppercase",letterSpacing:".05em",marginBottom:10}}>By Category</div>
                      {Object.entries(p.byCat).filter(([_,v])=>v.actual>0).sort((a,b)=>b[1].actual-a[1].actual).map(([cat,v])=>{
                        const ci=CATS[cat]||CATS.Other;
                        const pct=p.matActual>0?Math.round(v.actual/p.matActual*100):0;
                        return(
                          <div key={cat} style={{marginBottom:10}}>
                            <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:2}}>
                              <span style={{fontWeight:600}}>{ci.icon} {cat}</span>
                              <span style={{fontWeight:700,color:ci.color}}>{fmt(v.actual)}</span>
                            </div>
                            <div style={{height:6,background:"var(--stone-200)",borderRadius:99,overflow:"hidden"}}>
                              <div style={{height:"100%",width:`${pct}%`,background:ci.color,borderRadius:99}}/>
                            </div>
                            <div style={{fontSize:10,color:"var(--stone-400)",marginTop:1}}>{pct}% · Budget: {fmt(v.budget)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Project financials summary */}
                  <div style={{marginTop:16,borderTop:"2px solid var(--stone-200)",paddingTop:16}}>
                    <div style={{fontFamily:"var(--font-display)",fontSize:15,fontWeight:700,marginBottom:10}}>📊 Project Financials</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:"6px 20px",maxWidth:400}}>
                      {[
                        ["Original Quote",fmt(p.originalQuote)],
                        ...(p.changeOrder>0?[["Change Order",fmt(p.changeOrder)]]:[]),
                        ["Project Total",fmt(p.quote)],
                        ["─────────","──────"],
                        ["Material Budget",fmt(p.matBudget)],
                        ["Material Actual",fmt(p.matActual)],
                        ["Budget Variance",(p.matVariance>=0?"+":"")+fmt(p.matVariance)],
                        ["Labor",fmt(p.labor)],
                        ["─────────","──────"],
                        ["Total Cost",fmt(p.totalCost)],
                        ["Gross Profit",fmt(p.grossProfit)],
                      ].map(([label,val],i)=>{
                        if(label==="─────────") return <div key={i} style={{gridColumn:"1/-1",borderTop:"1px solid var(--stone-200)",margin:"4px 0"}}/>;
                        const isProfit=label==="Gross Profit";
                        const isVariance=label.includes("Variance");
                        return(
                          <React.Fragment key={i}>
                            <div style={{fontSize:12,color:"var(--stone-600)"}}>{label}</div>
                            <div style={{fontSize:13,fontWeight:isProfit?800:600,fontFamily:"var(--font-display)",textAlign:"right",
                              color:isProfit?(p.grossProfit>0?"var(--accent-green)":"var(--accent-red)"):isVariance?(p.matVariance>=0?"var(--accent-green)":"var(--accent-red)"):"var(--stone-800)"}}>{val}</div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Expanded but no budget */}
              {expanded && !p.hasBudget && (
                <div style={{background:"var(--stone-50)",padding:20}}>
                  <div style={{fontSize:12,color:"var(--stone-500)",display:"flex",alignItems:"center",gap:8}}>
                    <span>📝</span>
                    <span>This job doesn't have a Project Budget yet. Create one in <strong>Project Budgets</strong> and link it to this job for detailed phase and category breakdowns.</span>
                  </div>
                </div>
              )}

              {/* Margin badge footer */}
              <div style={{padding:"10px 20px",background:"var(--stone-50)",display:"flex",justifyContent:"space-between",alignItems:"center",borderTop:"1px solid var(--stone-100)"}}>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <div style={{background:gpColor+"18",border:`1px solid ${gpColor}33`,borderRadius:"var(--radius)",padding:"4px 12px"}}>
                    <span style={{fontFamily:"var(--font-display)",fontSize:15,fontWeight:700,color:gpColor}}>{p.margin}% margin</span>
                    <span style={{fontSize:11,color:gpColor,marginLeft:6,fontWeight:600}}>{marginLabel(p.margin)}</span>
                  </div>
                </div>
                <div style={{display:"flex",gap:12,alignItems:"center"}}>
                  {p.crew && <span style={{fontSize:11,color:"var(--stone-400)"}}>👷 {p.crew}</span>}
                  {p.location && <span style={{fontSize:11,color:"var(--stone-400)"}}>📍 {p.location}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </>)}

      {/* ════════════════════════════════════════════════════════ */}
      {/*  BY CATEGORY VIEW                                       */}
      {/* ════════════════════════════════════════════════════════ */}
      {view === "categories" && displayProjects.length>0 && (<>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:16}}>
          {Object.entries(catRollup).filter(([_,v])=>v.actual>0||v.budget>0).sort((a,b)=>b[1].actual-a[1].actual).map(([cat,v])=>{
            const ci=CATS[cat]||CATS.Other;
            const pctOfTotal=totMatActual>0?Math.round(v.actual/totMatActual*100):0;
            const variance=v.budget-v.actual;
            const projectList=[...v.projects];
            return(
              <div key={cat} style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",border:"1px solid var(--stone-200)",boxShadow:"var(--shadow-sm)",overflow:"hidden"}}>
                <div style={{padding:"16px 20px",borderBottom:"1px solid var(--stone-100)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:40,height:40,borderRadius:"var(--radius)",background:ci.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{ci.icon}</div>
                    <div>
                      <div style={{fontFamily:"var(--font-display)",fontSize:16,fontWeight:700}}>{cat}</div>
                      <div style={{fontSize:11,color:"var(--stone-400)"}}>{projectList.length} project{projectList.length!==1?"s":""}</div>
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontFamily:"var(--font-display)",fontSize:22,fontWeight:700,color:ci.color}}>{fmt(v.actual)}</div>
                    <div style={{fontSize:10,color:"var(--stone-400)"}}>{pctOfTotal}% of total spend</div>
                  </div>
                </div>
                <div style={{padding:"14px 20px"}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
                    <div style={{background:"var(--stone-100)",borderRadius:"var(--radius)",padding:10,textAlign:"center"}}>
                      <div style={{fontSize:10,color:"var(--stone-400)",textTransform:"uppercase",marginBottom:2}}>Budget</div>
                      <div style={{fontFamily:"var(--font-display)",fontSize:15,fontWeight:700}}>{fmt(v.budget)}</div>
                    </div>
                    <div style={{background:"var(--stone-100)",borderRadius:"var(--radius)",padding:10,textAlign:"center"}}>
                      <div style={{fontSize:10,color:"var(--stone-400)",textTransform:"uppercase",marginBottom:2}}>Actual</div>
                      <div style={{fontFamily:"var(--font-display)",fontSize:15,fontWeight:700,color:ci.color}}>{fmt(v.actual)}</div>
                    </div>
                    <div style={{background:variance>=0?"rgba(122,158,126,.06)":"rgba(192,97,74,.06)",borderRadius:"var(--radius)",padding:10,textAlign:"center"}}>
                      <div style={{fontSize:10,color:"var(--stone-400)",textTransform:"uppercase",marginBottom:2}}>Variance</div>
                      <div style={{fontFamily:"var(--font-display)",fontSize:15,fontWeight:700,color:variance>=0?"var(--accent-green)":"var(--accent-red)"}}>{(variance>=0?"+":"")+fmt(variance)}</div>
                    </div>
                  </div>
                  <div style={{fontSize:11,color:"var(--stone-400)"}}>Used in: {projectList.join(", ")}</div>
                </div>
              </div>
            );
          })}
        </div>
      </>)}

      {/* ════════════════════════════════════════════════════════ */}
      {/*  LABOR VIEW                                              */}
      {/* ════════════════════════════════════════════════════════ */}
      {view === "labor" && (<>
        {/* ── Labor KPIs ── */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(175px,1fr))",gap:14,marginBottom:24}}>
          {[
            {label:"Total Labor",     value:fmtK(laborData.totalLabor), color:"var(--warm-gold)", sub:`${laborData.byProject.length} projects with labor data`},
            {label:"Installers",      value:laborData.installerList.filter(i=>i.name!=="Unassigned").length, color:"var(--accent-blue)", sub:"Active on filtered projects"},
            {label:"Avg per Project", value:laborData.byProject.length>0?fmtK(Math.round(laborData.totalLabor/laborData.byProject.length)):fmtK(0), color:"var(--stone-700)", sub:"Across all projects"},
            {label:"Top Installer",   value:laborData.installerList[0]?.name||"—", color:"var(--accent-green)", sub:laborData.installerList[0]?fmtWhole(laborData.installerList[0].total):"No data"},
            {label:"% of Revenue",    value:current.totRevenue>0?Math.round(laborData.totalLabor/current.totRevenue*100)+"%":"0%", color:current.totRevenue>0&&laborData.totalLabor/current.totRevenue>0.35?"var(--accent-red)":"var(--accent-green)", sub:current.totRevenue>0&&laborData.totalLabor/current.totRevenue<=0.35?"✓ Healthy":"⚠ High"},
          ].map((s,i)=>(
            <div key={i} style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",padding:"20px 22px",border:"1px solid var(--stone-200)",boxShadow:"var(--shadow-sm)"}}>
              <div style={{fontFamily:"var(--font-display)",fontSize:28,fontWeight:700,color:s.color,lineHeight:1}}>{s.value}</div>
              <div style={{fontSize:12,color:"var(--stone-500)",fontWeight:500,letterSpacing:".04em",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
              <div style={{fontSize:11,color:"var(--stone-400)",marginTop:3}}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Labor by Installer ── */}
        <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",border:"1px solid var(--stone-200)",overflow:"hidden",marginBottom:20}}>
          <div style={{padding:"14px 20px",borderBottom:"1px solid var(--stone-100)",fontFamily:"var(--font-display)",fontSize:16,fontWeight:700}}>👷 Labor by Installer</div>
          <div style={{padding:"18px 20px"}}>
            {laborData.installerList.length === 0 ? (
              <div style={{textAlign:"center",padding:30,color:"var(--stone-400)",fontSize:13}}>
                <div style={{fontSize:28,marginBottom:8}}>👷</div>
                No installer assignments found. Use <strong>+ Assign Installer</strong> in Project Budgets to track who is earning what on each phase.
              </div>
            ) : (<>
              {/* Bar chart */}
              {laborData.installerList.map(inst => {
                const pct = laborData.totalLabor > 0 ? Math.round(inst.total / laborData.totalLabor * 100) : 0;
                const isUnassigned = inst.name === "Unassigned";
                return (
                  <div key={inst.name} style={{marginBottom:14}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:13,fontWeight:700,color:isUnassigned?"var(--stone-400)":"var(--stone-800)"}}>{inst.name}</span>
                        <span style={{fontSize:10,color:"var(--stone-400)"}}>{inst.projects} project{inst.projects!==1?"s":""}</span>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:11,color:"var(--stone-400)"}}>{pct}%</span>
                        <span style={{fontSize:14,fontWeight:700,fontFamily:"var(--font-display)",color:"var(--warm-gold)"}}>{fmtWhole(inst.total)}</span>
                      </div>
                    </div>
                    <div style={{height:10,background:"var(--stone-200)",borderRadius:99,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${pct}%`,background:isUnassigned?"var(--stone-400)":"var(--warm-gold)",borderRadius:99,transition:"width .5s"}}/>
                    </div>
                  </div>
                );
              })}

              {/* Total row */}
              <div style={{borderTop:"2px solid var(--stone-200)",paddingTop:12,marginTop:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:13,fontWeight:700}}>Total Labor</span>
                <span style={{fontFamily:"var(--font-display)",fontSize:22,fontWeight:700,color:"var(--warm-gold)"}}>{fmtWhole(laborData.totalLabor)}</span>
              </div>
            </>)}
          </div>
        </div>

        {/* ── Labor by Project ── */}
        {laborData.byProject.length > 0 && (
          <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",border:"1px solid var(--stone-200)",overflow:"hidden",marginBottom:20}}>
            <div style={{padding:"14px 20px",borderBottom:"1px solid var(--stone-100)",fontFamily:"var(--font-display)",fontSize:16,fontWeight:700}}>📋 Labor by Project</div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead>
                  <tr style={{background:"var(--stone-100)",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",color:"var(--stone-500)"}}>
                    <th style={{padding:"10px 16px",textAlign:"left"}}>Project</th>
                    <th style={{padding:"10px 16px",textAlign:"left"}}>Customer</th>
                    <th style={{padding:"10px 16px",textAlign:"left"}}>Installer Breakdown</th>
                    <th style={{padding:"10px 16px",textAlign:"right"}}>Total Labor</th>
                  </tr>
                </thead>
                <tbody>
                  {laborData.byProject.map(p => (
                    <tr key={p.id} style={{borderBottom:"1px solid var(--stone-100)"}}>
                      <td style={{padding:"12px 16px",fontWeight:600,fontSize:13}}>{p.name}</td>
                      <td style={{padding:"12px 16px",fontSize:12,color:"var(--stone-500)"}}>{p.customer}</td>
                      <td style={{padding:"12px 16px"}}>
                        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                          {p.installers.map(inst => (
                            <span key={inst.name} style={{
                              fontSize:11,fontWeight:600,padding:"3px 10px",borderRadius:99,
                              background:inst.name==="Unassigned"?"var(--stone-200)":"rgba(201,169,110,.12)",
                              color:inst.name==="Unassigned"?"var(--stone-500)":"var(--warm-gold)"
                            }}>
                              {inst.name}: {fmtWhole(inst.amount)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={{padding:"12px 16px",textAlign:"right",fontWeight:700,fontFamily:"var(--font-display)",fontSize:15,color:"var(--warm-gold)"}}>{fmtWhole(p.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{background:"var(--stone-100)",fontWeight:700,borderTop:"2px solid var(--stone-200)"}}>
                    <td colSpan={3} style={{padding:"11px 16px",fontSize:13,color:"var(--stone-600)"}}>Total ({laborData.byProject.length} projects)</td>
                    <td style={{padding:"11px 16px",textAlign:"right",fontFamily:"var(--font-display)",fontSize:16,color:"var(--warm-gold)"}}>{fmtWhole(laborData.totalLabor)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* ── Tip ── */}
        <div style={{padding:"13px 16px",background:"rgba(201,169,110,.07)",border:"1.5px solid var(--sand-300)",borderRadius:"var(--radius)",display:"flex",gap:10,alignItems:"flex-start"}}>
          <span style={{fontSize:16,flexShrink:0}}>💡</span>
          <div style={{fontSize:12,color:"var(--stone-600)",lineHeight:1.6}}>
            <strong>Labor tracking tip:</strong> Assign installers to each phase in <strong>Project Budgets</strong> using the <strong>+ Assign Installer</strong> button. This feeds the labor breakdown here automatically. Unassigned labor shows phases with costs but no installer selected. Use the period filters above to see labor costs by month, quarter, or year.
          </div>
        </div>
      </>)}

      {/* ════════════════════════════════════════════════════════ */}
      {/*  EBITDA VIEW                                             */}
      {/* ════════════════════════════════════════════════════════ */}
      {view === "ebitda" && (<>
        {/* EBITDA KPIs */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(175px,1fr))",gap:14,marginBottom:24}}>
          {[
            {label:"Revenue",        value:fmtK(current.totRevenue),      color:"var(--accent-blue)",  sub:`${current.count} projects` },
            {label:"COGS",           value:fmtK(current.totCost),         color:"var(--accent-red)",   sub:"Materials + Labor" },
            {label:"Gross Profit",   value:fmtK(current.totGP),           color:current.totGP>0?"var(--accent-green)":"var(--accent-red)", sub:`${avgMargin}% margin` },
            {label:"Operating Exp.", value:fmtK(ebitdaTotals.totalOpex),  color:"var(--accent-amber)", sub:opexReady?"From expense entries":"Table not configured" },
            {label:"EBITDA",         value:fmtK(ebitdaTotals.ebitda),     color:ebitdaTotals.ebitda>0?"var(--accent-green)":"var(--accent-red)", sub:`${ebitdaTotals.ebitdaMargin}% of revenue` },
          ].map((s,i)=>(
            <div key={i} style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",padding:"20px 22px",border:"1px solid var(--stone-200)",boxShadow:"var(--shadow-sm)"}}>
              <div style={{fontFamily:"var(--font-display)",fontSize:32,fontWeight:700,color:s.color,lineHeight:1}}>{s.value}</div>
              <div style={{fontSize:12,color:"var(--stone-500)",fontWeight:500,letterSpacing:".04em",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
              <div style={{fontSize:11,color:"var(--stone-400)",marginTop:3}}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* EBITDA Waterfall */}
        <div style={{background:"var(--stone-800)",borderRadius:"var(--radius-lg)",padding:24,marginBottom:20,color:"white"}}>
          <div style={{fontFamily:"var(--font-display)",fontSize:18,fontWeight:700,marginBottom:16}}>📊 EBITDA Waterfall — {getPeriodLabel(period)}</div>
          <div style={{display:"flex",alignItems:"flex-end",gap:2,height:140,marginBottom:12}}>
            {[
              {label:"Revenue",     value:current.totRevenue,       color:"var(--accent-blue)"},
              {label:"Materials",   value:-current.totMatActual,    color:"var(--accent-red)"},
              {label:"Labor",       value:-current.totLabor,        color:"var(--warm-gold)"},
              {label:"Gross Profit",value:current.totGP,            color:current.totGP>0?"var(--accent-green)":"var(--accent-red)"},
              {label:"OpEx",        value:-ebitdaTotals.totalOpex,  color:"var(--accent-amber)"},
              {label:"EBITDA",      value:ebitdaTotals.ebitda,      color:ebitdaTotals.ebitda>0?"#4ade80":"var(--accent-red)"},
            ].map((bar,i)=>{
              const maxVal=Math.max(current.totRevenue,1);
              const h=Math.max(Math.round(Math.abs(bar.value)/maxVal*120),8);
              return(
                <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                  <div style={{fontSize:12,fontFamily:"var(--font-display)",fontWeight:700,color:bar.color}}>{fmtK(Math.abs(bar.value))}</div>
                  <div style={{width:"65%",height:h,background:bar.color,borderRadius:"4px 4px 0 0",transition:"height .5s ease"}}/>
                  <div style={{fontSize:9,opacity:.6,textAlign:"center"}}>{bar.value<0?"−":""}{bar.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Monthly Breakdown Table */}
        <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",border:"1px solid var(--stone-200)",overflow:"hidden",marginBottom:20}}>
          <div style={{padding:"14px 20px",borderBottom:"1px solid var(--stone-100)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontFamily:"var(--font-display)",fontSize:16,fontWeight:700}}>📅 Monthly P&L Breakdown</div>
            <button onClick={()=>{
              const now=new Date();
              openOpexModal(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
            }} style={{padding:"6px 14px",borderRadius:"var(--radius)",border:"1.5px solid var(--warm-gold)",background:"rgba(201,169,110,.08)",color:"var(--warm-gold)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
              + Add Expenses
            </button>
          </div>
          {monthlyData.length>0 ? (
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead>
                  <tr style={{background:"var(--stone-100)",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",color:"var(--stone-500)"}}>
                    {["Month","Revenue","Materials","Labor","Gross Profit","OpEx","EBITDA","Jobs",""].map(h=>(
                      <th key={h} style={{padding:"10px 14px",textAlign:h==="Month"||h===""?"left":"right",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthlyData.map(m=>{
                    const gpColor=m.grossProfit>0?"var(--accent-green)":"var(--accent-red)";
                    const eColor=m.ebitda>0?"var(--accent-green)":"var(--accent-red)";
                    return(
                      <tr key={m.month} style={{borderBottom:"1px solid var(--stone-100)",fontSize:13}}>
                        <td style={{padding:"12px 14px",fontWeight:600}}>{m.label}</td>
                        <td style={{padding:"12px 14px",textAlign:"right",fontWeight:600,color:"var(--accent-blue)"}}>{fmtWhole(m.revenue)}</td>
                        <td style={{padding:"12px 14px",textAlign:"right",color:"var(--accent-red)"}}>{fmtWhole(m.materials)}</td>
                        <td style={{padding:"12px 14px",textAlign:"right",color:"var(--warm-gold)"}}>{fmtWhole(m.labor)}</td>
                        <td style={{padding:"12px 14px",textAlign:"right",fontWeight:700,color:gpColor}}>{fmtWhole(m.grossProfit)}</td>
                        <td style={{padding:"12px 14px",textAlign:"right",color:m.totalOpex>0?"var(--accent-amber)":"var(--stone-300)"}}>{m.totalOpex>0?fmtWhole(m.totalOpex):"—"}</td>
                        <td style={{padding:"12px 14px",textAlign:"right",fontWeight:800,fontFamily:"var(--font-display)",fontSize:15,color:eColor}}>{fmtWhole(m.ebitda)}</td>
                        <td style={{padding:"12px 14px",textAlign:"right",fontSize:11,color:"var(--stone-400)"}}>{m.projects}</td>
                        <td style={{padding:"12px 14px"}}>
                          <button onClick={()=>openOpexModal(m.month)}
                            style={{padding:"3px 8px",borderRadius:4,border:"1px solid var(--stone-200)",background:"white",fontSize:10,cursor:"pointer",color:"var(--stone-500)"}}>
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{background:"var(--stone-100)",fontWeight:800,fontSize:13,borderTop:"2px solid var(--stone-200)"}}>
                    <td style={{padding:"12px 14px",color:"var(--stone-600)"}}>Totals</td>
                    <td style={{padding:"12px 14px",textAlign:"right",color:"var(--accent-blue)"}}>{fmtWhole(current.totRevenue)}</td>
                    <td style={{padding:"12px 14px",textAlign:"right",color:"var(--accent-red)"}}>{fmtWhole(current.totMatActual)}</td>
                    <td style={{padding:"12px 14px",textAlign:"right",color:"var(--warm-gold)"}}>{fmtWhole(current.totLabor)}</td>
                    <td style={{padding:"12px 14px",textAlign:"right",color:current.totGP>0?"var(--accent-green)":"var(--accent-red)"}}>{fmtWhole(current.totGP)}</td>
                    <td style={{padding:"12px 14px",textAlign:"right",color:"var(--accent-amber)"}}>{fmtWhole(ebitdaTotals.totalOpex)}</td>
                    <td style={{padding:"12px 14px",textAlign:"right",fontFamily:"var(--font-display)",fontSize:16,color:ebitdaTotals.ebitda>0?"var(--accent-green)":"var(--accent-red)"}}>{fmtWhole(ebitdaTotals.ebitda)}</td>
                    <td style={{padding:"12px 14px",textAlign:"right",fontSize:11,color:"var(--stone-400)"}}>{current.count}</td>
                    <td/>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div style={{padding:40,textAlign:"center"}}>
              <div style={{fontSize:32,marginBottom:8}}>📅</div>
              <div style={{fontSize:13,color:"var(--stone-500)"}}>No monthly data available for this period.</div>
            </div>
          )}
        </div>

        {/* Operating Expenses Breakdown */}
        {ebitdaTotals.totalOpex>0 && (
          <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",border:"1px solid var(--stone-200)",overflow:"hidden",marginBottom:20}}>
            <div style={{padding:"14px 20px",borderBottom:"1px solid var(--stone-100)",fontFamily:"var(--font-display)",fontSize:16,fontWeight:700}}>🏢 Operating Expense Breakdown</div>
            <div style={{padding:"18px 20px"}}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12}}>
                {OPEX_CATS.map(c=>{
                  const total = opex.filter(e=>e.category===c.id).reduce((s,e)=>s+Number(e.amount||0),0);
                  if(total===0) return null;
                  const pct = ebitdaTotals.totalOpex>0?Math.round(total/ebitdaTotals.totalOpex*100):0;
                  return(
                    <div key={c.id} style={{background:"var(--stone-100)",borderRadius:"var(--radius)",padding:"14px 16px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                        <span style={{fontSize:12,fontWeight:600}}>{c.icon} {c.label}</span>
                        <span style={{fontSize:11,color:"var(--stone-400)"}}>{pct}%</span>
                      </div>
                      <div style={{fontFamily:"var(--font-display)",fontSize:20,fontWeight:700,color:c.color}}>{fmtWhole(total)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Setup notice if table doesn't exist */}
        {!opexReady && (
          <div style={{padding:"16px 20px",background:"rgba(201,169,110,.07)",border:"1.5px solid var(--sand-300)",borderRadius:"var(--radius)",display:"flex",gap:10,alignItems:"flex-start",marginBottom:20}}>
            <span style={{fontSize:16,flexShrink:0}}>💡</span>
            <div style={{fontSize:12,color:"var(--stone-600)",lineHeight:1.6}}>
              <strong>EBITDA setup:</strong> To track operating expenses (vehicle, insurance, tools, etc.), create the <code>operating_expenses</code> table in Supabase. This enables full EBITDA reporting with monthly expense tracking. Gross Profit is available now from your existing project data.
            </div>
          </div>
        )}

        {/* Tip */}
        <div style={{padding:"13px 16px",background:"rgba(201,169,110,.07)",border:"1.5px solid var(--sand-300)",borderRadius:"var(--radius)",display:"flex",gap:10,alignItems:"flex-start"}}>
          <span style={{fontSize:16,flexShrink:0}}>💡</span>
          <div style={{fontSize:12,color:"var(--stone-600)",lineHeight:1.6}}>
            <strong>EBITDA tip:</strong> Enter monthly operating expenses (vehicle, insurance, tools, marketing, etc.) using the <strong>+ Add Expenses</strong> button or click <strong>Edit</strong> on any month row. Revenue and COGS flow automatically from your jobs and project budgets. For the most accurate picture, set expenses monthly and review quarterly.
          </div>
        </div>
      </>)}

      {/* ════════════════════════════════════════════════════════ */}
      {/*  OPEX EDIT MODAL                                         */}
      {/* ════════════════════════════════════════════════════════ */}
      {opexModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999}} onClick={()=>setOpexModal(null)}>
          <div onClick={e=>e.stopPropagation()} style={{background:"white",borderRadius:"var(--radius-lg)",width:"100%",maxWidth:560,maxHeight:"85vh",overflow:"auto",boxShadow:"var(--shadow-lg)",animation:"modalIn .2s ease"}}>
            <div style={{padding:"18px 24px",borderBottom:"1px solid var(--stone-200)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontFamily:"var(--font-display)",fontSize:20,fontWeight:700}}>Operating Expenses</div>
                <div style={{fontSize:12,color:"var(--stone-500)",marginTop:2}}>{formatMonth(opexModal)}</div>
              </div>
              <button onClick={()=>setOpexModal(null)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"var(--stone-400)"}}>✕</button>
            </div>
            <div style={{padding:"20px 24px"}}>
              {/* Month selector */}
              <div style={{marginBottom:20}}>
                <label style={{fontSize:11,fontWeight:600,color:"var(--stone-500)",textTransform:"uppercase",letterSpacing:".05em",marginBottom:4,display:"block"}}>Month</label>
                <input type="month" value={opexModal} onChange={e=>{ if(e.target.value) { const newMonth=e.target.value; setOpexModal(null); setTimeout(()=>openOpexModal(newMonth),50); }}}
                  style={{padding:"8px 12px",borderRadius:"var(--radius)",border:"1.5px solid var(--stone-200)",fontSize:13,fontFamily:"var(--font-body)",width:"100%"}}/>
              </div>

              {/* Expense entries */}
              {OPEX_CATS.map(c=>(
                <div key={c.id} style={{marginBottom:14,display:"grid",gridTemplateColumns:"1fr 120px",gap:8,alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:"var(--stone-700)",display:"flex",alignItems:"center",gap:6}}>
                      <span>{c.icon}</span> {c.label}
                    </div>
                  </div>
                  <input type="number" placeholder="$0.00" value={opexForm[c.id]?.amount||''}
                    onChange={e=>setOpexForm(prev=>({...prev,[c.id]:{...prev[c.id],amount:e.target.value}}))}
                    style={{padding:"8px 10px",borderRadius:"var(--radius)",border:"1.5px solid var(--stone-200)",fontSize:13,textAlign:"right",fontFamily:"var(--font-body)"}}/>
                </div>
              ))}

              {/* Total */}
              <div style={{borderTop:"2px solid var(--stone-200)",paddingTop:12,marginTop:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:13,fontWeight:700,color:"var(--stone-700)"}}>Monthly Total</span>
                <span style={{fontFamily:"var(--font-display)",fontSize:22,fontWeight:700,color:"var(--accent-amber)"}}>
                  {fmtWhole(OPEX_CATS.reduce((s,c)=>s+(Number(opexForm[c.id]?.amount)||0),0))}
                </span>
              </div>
            </div>
            <div style={{padding:"14px 24px",borderTop:"1px solid var(--stone-200)",display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>setOpexModal(null)}
                style={{padding:"8px 18px",borderRadius:"var(--radius)",border:"1.5px solid var(--stone-200)",background:"white",color:"var(--stone-600)",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                Cancel
              </button>
              <button onClick={saveOpex} disabled={opexSaving}
                style={{padding:"8px 22px",borderRadius:"var(--radius)",border:"none",background:"var(--stone-800)",color:"white",fontSize:13,fontWeight:600,cursor:"pointer",opacity:opexSaving?.6:1}}>
                {opexSaving?"Saving...":"Save Expenses"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes modalIn{from{opacity:0;transform:scale(.97)}to{opacity:1;transform:scale(1)}}`}</style>
    </div>
  );
}
